/**
 * Policies list — active filters bar (OR-1664).
 *
 * Same behaviour as Submission Triage: renders under the toolbar when any
 * filter is active, with a result count, a removable chip per clause and
 * "Clear All". Removing a chip commits the reduced filter set.
 */
import { XClose } from "@untitledui/icons";
import { cx } from "@tricura/atlas-ui";
import { PRESET_LABELS, PRIORITY_LABELS, type PolicyFilters, STATUS_LABELS, countActiveFilters } from "./filters";

interface ActiveFiltersBarProps {
    filters: PolicyFilters;
    /** Total rows matching the committed filters. */
    total: number;
    onChange: (next: PolicyFilters) => void;
    onClearAll: () => void;
}

interface Chip {
    key: string;
    label: string;
    next: PolicyFilters;
}

/** ISO "2026-01-01" → "01/01/2026"; "" → "Any". */
function dateLabel(iso: string): string {
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[2]}/${m[3]}/${m[1]}` : "Any";
}

// eslint-disable-next-line react-refresh/only-export-components -- chip vocabulary co-located with the bar
export function buildChips(f: PolicyFilters): Chip[] {
    const chips: Chip[] = [];
    for (const p of f.priorities) {
        chips.push({ key: `priority:${p}`, label: `Priority: ${PRIORITY_LABELS[p]}`, next: { ...f, priorities: f.priorities.filter((x) => x !== p) } });
    }
    for (const st of f.statuses) {
        chips.push({ key: `status:${st}`, label: `Status: ${STATUS_LABELS[st]}`, next: { ...f, statuses: f.statuses.filter((x) => x !== st) } });
    }
    if (f.inceptionStart || f.inceptionEnd) {
        chips.push({
            key: "inception",
            label: `Inception Date: ${dateLabel(f.inceptionStart)} - ${dateLabel(f.inceptionEnd)}`,
            next: { ...f, inceptionStart: "", inceptionEnd: "" },
        });
    }
    if (f.expirationPreset) {
        chips.push({ key: "expiration", label: `Expiration Date: ${PRESET_LABELS[f.expirationPreset]}`, next: { ...f, expirationPreset: "" } });
    } else if (f.expirationStart || f.expirationEnd) {
        chips.push({
            key: "expiration",
            label: `Expiration Date: ${dateLabel(f.expirationStart)} - ${dateLabel(f.expirationEnd)}`,
            next: { ...f, expirationStart: "", expirationEnd: "" },
        });
    }
    if (f.brokerage) chips.push({ key: "brokerage", label: `Brokerage: ${f.brokerage.label}`, next: { ...f, brokerage: null } });
    return chips;
}

/** The design's Tag/sm: 24px tall, 4px radius, 1px border, asymmetric
 *  padding because the close glyph sits on the right. */
const CHIP =
    "border-primary bg-primary text-secondary hover:bg-secondary inline-flex h-6 items-center gap-[3px] rounded-sm border pl-2 pr-1 text-xs font-medium transition duration-100 ease-linear";

export function ActiveFiltersBar({ filters, total, onChange, onClearAll }: ActiveFiltersBarProps) {
    if (countActiveFilters(filters) === 0) return null;
    const chips = buildChips(filters);

    return (
        <div className="border-secondary flex flex-wrap items-center gap-2 border-b px-4 py-3" data-autotest="policies-active-filters-bar">
            <span className="text-tertiary text-sm">
                Showing <span className="text-primary font-semibold">{total}</span> results
            </span>
            {chips.map((chip) => (
                <button key={chip.key} type="button" onClick={() => onChange(chip.next)} className={cx(CHIP, "cursor-pointer")}>
                    {chip.label}
                    <XClose aria-hidden="true" className="text-fg-quaternary size-3.5" />
                </button>
            ))}
            <button type="button" onClick={onClearAll} className="text-tertiary hover:text-primary ml-1 cursor-pointer text-xs font-semibold">
                Clear All
            </button>
        </div>
    );
}
